import { styled } from "@linaria/react"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router"
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools"
import { debounce } from "lodash-es"
import { useCallback, useEffect, useState } from "react"

import { Button } from "@/components/Button/Button.tsx"
import { Centered } from "@/components/Centered/Centered.ts"
import { Horizontal, Spring, Vertical } from "@/components/Flex.ts"
import { Heading4, Heading5 } from "@/components/Heading/Heading.tsx"
import { GlobalLoader } from "@/components/Loader/GlobalLoader.tsx"
import { SecondaryTextSpan } from "@/components/SecondaryTextSpan/SecondaryTextSpan.ts"
import { useOrgContext, useRootContext } from "@/hooks/useRootContext.ts"
import { Icon20, LogoutRegular } from "@/icons/Icons.ts"
import { startViewTransition } from "@/lib/dom.ts"
import { MatterLogoAndName } from "@/routes/auth/MatterLogoAndName.tsx"
import { PageBackgroundGradient } from "@/routes/auth/PageBackgroundGradient.ts"
import { PageBackground, PageContainer, PageContent } from "@/routes/auth/PageStyles.ts"
import { Navigator } from "@/routes/Navigator.ts"
import { ellipsis } from "@/styles/mixins.ts"
import { smallFontSize } from "@/styles/textStyles.ts"

import type { ButtonKind } from "@/components/Button/Button.tsx"
import type { MyOrgs, OrgInvitationRef, OrgRef } from "@/routes/root/MyOrgsRoute.tsx"
import type { MouseEvent, PropsWithChildren } from "react"

function useMyOrgs(): MyOrgs {
  return getRouteApi("/").useLoaderData().myOrgs
}

enum OrgBlock {
  Invitations = "Invitations",
  Workspaces = "Your workspaces",
  Other = "Other workspaces you can join",
}

export const MyOrgsPage = () => {
  const context = useRootContext()
  const myOrgs = useMyOrgs()
  const me = context.authController.authenticatedUser()

  const [isScrollable, setIsScrollable] = useState(false)

  useEffect(() => {
    const checkScrollable = debounce(() => {
      setIsScrollable(document.body.scrollHeight > window.innerHeight)
    }, 32)

    checkScrollable()

    window.addEventListener("resize", checkScrollable)

    return () => {
      window.removeEventListener("resize", checkScrollable)
    }
  }, [myOrgs])

  return (
    <>
      {context.useLoader && <GlobalLoader />}

      <PageContainer>
        <PageContent twoPaneLayout={false}>
          <OrgsCentered gap="16px" isScrollable={isScrollable}>
            <MatterLogoAndName />

            {me && (
              <WelcomeHeading>
                {myOrgs.hasAccountIn.length > 0 ? `Welcome back, ${me.first_name}!` : `Welcome to Matter, ${me.first_name}!`}
              </WelcomeHeading>
            )}

            {myOrgs.hasInvitationTo.length > 0 && (
              <OrgsBlock
                title={OrgBlock.Invitations}
                refs={myOrgs.hasInvitationTo}
              />
            )}

            {myOrgs.hasAccountIn.length > 0 && (
              <OrgsBlock
                title={OrgBlock.Workspaces}
                refs={myOrgs.hasAccountIn}
              />
            )}

            {myOrgs.canSignUpTo.length > 0 && (
              <OrgsBlock
                title={OrgBlock.Other}
                refs={myOrgs.canSignUpTo}
              />
            )}

            <Spring />

            <WelcomeFooter>
              {me && <FooterName>{me.full_name}</FooterName>}
              {me && <FooterEmail>{me.email}</FooterEmail>}
              <Logout />
            </WelcomeFooter>
          </OrgsCentered>
        </PageContent>
      </PageContainer>

      <PageBackground background={PageBackgroundGradient.Orgs} />

      {window.parent === window && import.meta.env.DEV && <TanStackRouterDevtools position="bottom-right" />}
      {window.parent === window && import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </>
  )
}

type OrgsBlockProps = PropsWithChildren & {
  title: OrgBlock
  refs: OrgRef[] | OrgInvitationRef[]
}

function orgName(ref: OrgRef) {
  return ref.name.length > 0 ? ref.name : ref.id
}

const OrgsBlock = (props: OrgsBlockProps) => {
  let buttonKind: ButtonKind | undefined
  if (props.title === OrgBlock.Invitations) {
    buttonKind = "success"
  } else if (props.title === OrgBlock.Workspaces) {
    buttonKind = "primary"
  } else if (props.title === OrgBlock.Other) {
    buttonKind = "secondary"
  }

  let buttonText: string | undefined
  if (props.title === OrgBlock.Invitations) {
    buttonText = "Accept"
  } else if (props.title === OrgBlock.Workspaces) {
    buttonText = "Open"
  } else if (props.title === OrgBlock.Other) {
    buttonText = "Join"
  }

  return (
    <Vertical gap="8px" data-testid={props.title}>
      <OrgsBlockTitle>{props.title}</OrgsBlockTitle>

      {props.refs && props.refs.length > 0 ? (
        <Vertical gap="24px">
          {props.refs.map((ref) => {
            const isInvitation = "invitationToken" in ref
            const location = isInvitation ? Navigator.invite(ref.invitationToken, ref.id) : Navigator.orgHome(ref.id)

            return (
              <OrgCard key={ref.id} isInvitation={isInvitation} data-testid={orgName(ref)}>
                <OrgName>
                  {orgName(ref)}
                </OrgName>

                <Link {...location} viewTransition>
                  <Button size="large" kind={buttonKind} data-testid={buttonText} tabIndex={-1}>
                    {buttonText}
                  </Button>
                </Link>
              </OrgCard>
            )
          })}
        </Vertical>
      ) : (
        <SecondaryTextSpan>No organizations found</SecondaryTextSpan>
      )}
    </Vertical>
  )
}

const Logout = () => {
  const context = useOrgContext()
  const navigate = useNavigate()

  const logout = useCallback(async (e: MouseEvent) => {
    e.preventDefault()
    context.persistence.clear()
    await context.authController.logout()
    startViewTransition(() => navigate(Navigator.login({ replace: true })))
  }, [context.authController, context.persistence, navigate])

  return (
    <LogoutLink onClick={logout} href="#">
      <LogoutRegular className={Icon20} />
      Log out
    </LogoutLink>
  )
}

const OrgsCentered = styled(Centered)<{ isScrollable: boolean }>`
  view-transition-name: orgs-list;

  padding-top: ${props => props.isScrollable ? "32px" : "128px"};
  text-align: center;
`

const OrgsBlockTitle = styled(Heading5)`
  margin-top: 16px;
`

const OrgCard = styled(Horizontal)<{ isInvitation: boolean }>`
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  width: min(60vw, 480px);

  padding: 16px 24px;

  background: ${props => props.isInvitation ? "radial-gradient(44.33% 100% at 51.79% 100%, #DDFFEA 0%, #F6FFF5 100%)" : "var(--background-color)"};
  box-shadow: 0 4px 20px 0 rgb(26 46 160 / 10%);
  border-radius: var(--border-radius-16);

  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 20px 0 rgb(26 46 160 / 18%);
  }
`

const OrgName = styled(Heading4)`
  ${ellipsis};
`

const WelcomeHeading = styled.h1`
  max-width: 600px;
  margin: 0;

  font-size: 54px;
  font-weight: 600;
  line-height: 60px;
  letter-spacing: -0.036px;

  text-align: center;
`

const WelcomeFooter = styled(Vertical)`
  padding-bottom: 24px;
`

const FooterName = styled.span`
  font-weight: 500;
`

const FooterEmail = styled.span`

`

const LogoutLink = styled.a`
  ${smallFontSize};

  display: flex;
  align-items: center;
  align-self: center;

  margin-top: 8px;
`